import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Radio,
  MapPin,
  Calculator,
  Wrench,
  Shield,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Bell,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/store/auth.store';
import { UserRole } from '@/types';
import { useState } from 'react';

interface NavItem {
  labelKey: string;
  href: string;
  icon: React.ElementType;
  roles?: string[];
  children?: { labelKey: string; href: string }[];
}

const navigation: NavItem[] = [
  {
    labelKey: 'nav.dashboards',
    href: '/dashboard',
    icon: LayoutDashboard,
    children: [
      { labelKey: 'nav.executive', href: '/dashboard/ejecutivo' },
      { labelKey: 'nav.operative', href: '/dashboard/operativo' },
      { labelKey: 'nav.accounting', href: '/dashboard/contable' },
    ],
  },
  {
    labelKey: 'nav.assets',
    href: '/activos',
    icon: Package,
    roles: [
      UserRole.SYSADMIN,
      UserRole.ASSET_ADMIN,
      UserRole.FIELD_OP,
      UserRole.STORE_MGR,
      UserRole.TECH,
      UserRole.AUDITOR,
    ],
    children: [
      { labelKey: 'nav.assetList', href: '/activos' },
      { labelKey: 'nav.newAsset', href: '/activos/nuevo' },
      { labelKey: 'nav.importAssets', href: '/activos/importar' },
      { labelKey: 'nav.disposals', href: '/activos/bajas' },
    ],
  },
  {
    labelKey: 'nav.rfid',
    href: '/rfid',
    icon: Radio,
    roles: [UserRole.SYSADMIN, UserRole.ASSET_ADMIN, UserRole.FIELD_OP, UserRole.TECH],
    children: [
      { labelKey: 'nav.assignTags', href: '/rfid/asignar' },
      { labelKey: 'nav.rfidInventory', href: '/rfid/inventario' },
      { labelKey: 'nav.devices', href: '/rfid/dispositivos' },
    ],
  },
  {
    labelKey: 'nav.traceability',
    href: '/trazabilidad',
    icon: MapPin,
    roles: [
      UserRole.SYSADMIN,
      UserRole.ASSET_ADMIN,
      UserRole.FIELD_OP,
      UserRole.STORE_MGR,
      UserRole.AUDITOR,
    ],
    children: [
      { labelKey: 'nav.movements', href: '/trazabilidad/movimientos' },
      { labelKey: 'nav.transfers', href: '/trazabilidad/traslados' },
      { labelKey: 'nav.map', href: '/trazabilidad/mapa' },
      { labelKey: 'nav.loans', href: '/trazabilidad/prestamos' },
    ],
  },
  {
    labelKey: 'nav.depreciation',
    href: '/depreciacion',
    icon: Calculator,
    roles: [UserRole.SYSADMIN, UserRole.ACCOUNTANT, UserRole.AUDITOR],
    children: [
      { labelKey: 'nav.depreciationRecords', href: '/depreciacion' },
      { labelKey: 'nav.calculate', href: '/depreciacion/calcular' },
      { labelKey: 'nav.accountingEntries', href: '/depreciacion/asientos' },
    ],
  },
  {
    labelKey: 'nav.maintenance',
    href: '/mantenimiento',
    icon: Wrench,
    roles: [UserRole.SYSADMIN, UserRole.ASSET_ADMIN, UserRole.TECH],
    children: [
      { labelKey: 'nav.workOrders', href: '/mantenimiento/ordenes' },
      { labelKey: 'nav.plans', href: '/mantenimiento/planes' },
    ],
  },
  {
    labelKey: 'nav.audit',
    href: '/auditoria',
    icon: Shield,
    roles: [UserRole.SYSADMIN, UserRole.AUDITOR],
    children: [
      { labelKey: 'nav.auditLog', href: '/auditoria/log' },
      { labelKey: 'nav.reconciliation', href: '/auditoria/conciliacion' },
    ],
  },
  {
    labelKey: 'nav.reports',
    href: '/reportes',
    icon: BarChart3,
    children: [
      { labelKey: 'nav.allReports', href: '/reportes' },
      { labelKey: 'nav.niifReports', href: '/reportes/niif' },
    ],
  },
  {
    labelKey: 'nav.notifications',
    href: '/notificaciones',
    icon: Bell,
    children: [{ labelKey: 'nav.notificationsList', href: '/notificaciones' }],
  },
  {
    labelKey: 'nav.admin',
    href: '/admin',
    icon: Settings,
    roles: [UserRole.SYSADMIN],
    children: [
      { labelKey: 'nav.users', href: '/admin/usuarios' },
      { labelKey: 'nav.locations', href: '/admin/ubicaciones' },
      { labelKey: 'nav.categories', href: '/admin/categorias' },
      { labelKey: 'nav.forms', href: '/admin/formularios' },
      { labelKey: 'nav.systemConfig', href: '/admin/configuracion' },
    ],
  },
];

export function Sidebar() {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState<Set<string>>(new Set());
  const location = useLocation();
  const { hasAnyRole, user } = useAuthStore();

  const toggleMenu = (key: string) => {
    setOpenMenus((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const filteredNav = navigation.filter(
    (item) => !item.roles || hasAnyRole(item.roles)
  );

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r bg-card transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
      role="navigation"
      aria-label={t('nav.mainNav')}
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between border-b px-3">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <Package className="h-6 w-6 text-primary" aria-hidden="true" />
            <div>
              <span className="text-lg font-bold text-primary tracking-tight leading-none">AT360</span>
              <p className="text-[10px] text-muted-foreground leading-none mt-0.5">by WM Wireless Mobile</p>
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-lg hover:bg-accent transition-colors"
          aria-label={collapsed ? t('nav.expandMenu') : t('nav.collapseMenu')}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-3">
        <ul className="space-y-0.5" role="list">
          {filteredNav.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            const isOpen = openMenus.has(item.labelKey) || isActive;
            const Icon = item.icon;
            const label = t(item.labelKey);

            return (
              <li key={item.labelKey}>
                {collapsed ? (
                  <NavLink
                    to={item.children?.[0]?.href ?? item.href}
                    className={cn(
                      'flex h-11 w-11 items-center justify-center rounded-lg mx-auto transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                    )}
                    title={label}
                    aria-label={label}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </NavLink>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleMenu(item.labelKey)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors min-h-[44px]',
                        isActive
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                      )}
                      aria-expanded={isOpen}
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                      <span className="flex-1 text-left">{label}</span>
                      <ChevronRight
                        className={cn(
                          'h-4 w-4 transition-transform duration-200',
                          isOpen && 'rotate-90'
                        )}
                        aria-hidden="true"
                      />
                    </button>
                    {isOpen && item.children && (
                      <ul className="ml-8 mt-0.5 space-y-0.5" role="list">
                        {item.children.map((child) => (
                          <li key={child.href}>
                            <NavLink
                              to={child.href}
                              className={({ isActive: active }) =>
                                cn(
                                  'block rounded-lg px-3 py-2 text-sm transition-colors min-h-[44px] flex items-center',
                                  active
                                    ? 'bg-primary text-primary-foreground font-medium'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                                )
                              }
                            >
                              {t(child.labelKey)}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="border-t px-4 py-3 space-y-1">
          {user?.companyId && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-mono font-semibold text-primary">
                {user.companyId}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                {user.tenantName ?? user.companyId}
              </span>
            </div>
          )}
          <p className="text-xs text-muted-foreground font-mono">
            {t('common.version')}
          </p>
        </div>
      )}
    </aside>
  );
}
